// Convert RichTextEditor HTML to a plain-text snippet.
//
// Used wherever rich text must render as plain text rather than formatted HTML:
// activity-log diffs, terse inline labels, etc. Strips tags, decodes the
// entities Quill emits (&nbsp; &amp; …), and collapses whitespace (including
// non-breaking spaces) so long content wraps naturally.
//
// Regex-based on purpose — it never touches innerHTML, so it can't trigger an
// <img onerror> or any other side effect, and it works without a DOM. A plain
// string with no tags comes back unchanged.
export function htmlToText(html) {
  if (html === null || html === undefined || html === '') return '';
  let s = String(html);
  s = s.replace(/<[^>]*>/g, ' '); // strip tags
  const named = {
    '&nbsp;': ' ', '&amp;': '&', '&lt;': '<', '&gt;': '>',
    '&quot;': '"', '&#39;': "'", '&apos;': "'",
  };
  s = s.replace(/&nbsp;|&amp;|&lt;|&gt;|&quot;|&#39;|&apos;/g, (m) => named[m]);
  s = s.replace(/&#(\d+);/g, (_, n) => {
    const code = Number(n);
    return Number.isFinite(code) ? String.fromCharCode(code) : '';
  });
  return s.replace(/\s+/g, ' ').trim();
}

// Render one value (string / number / object) to readable text.
function formatOne(item) {
  if (item === null || item === undefined) return '';
  if (typeof item === 'boolean') return item ? 'Yes' : 'No';
  if (typeof item !== 'object') return htmlToText(String(item));
  // Objects (e.g. a contact {name,email}) — prefer human fields, then a label,
  // else compact key:value pairs. Never "[object Object]".
  const named = [item.name, item.email].filter(Boolean).join(' — ');
  if (named) return named;
  if (item.label) return htmlToText(String(item.label));
  const pairs = Object.entries(item)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`);
  return pairs.join(', ');
}

// Format any activity-log change value for display: strings (incl. HTML) → plain
// text, booleans → Yes/No, arrays/objects → a readable summary. Returns '' for
// empty so callers can render a "—". Guarantees a value never shows as
// "[object Object]".
export function formatChangeValue(v) {
  if (v === null || v === undefined) return '';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'number') return String(v);
  if (typeof v === 'string') {
    const t = v.trim();
    // A value already stringified to "[object Object]" carries no data — hide it.
    if (/^\[object Object\]/.test(t)) return '';
    // Revive JSON-encoded arrays/objects so they render readably instead of raw.
    if ((t.startsWith('[') && t.endsWith(']')) || (t.startsWith('{') && t.endsWith('}'))) {
      try { return formatChangeValue(JSON.parse(t)); } catch { /* not JSON — fall through */ }
    }
    return htmlToText(v);
  }
  if (Array.isArray(v)) return v.map(formatOne).filter(Boolean).join(', ');
  if (typeof v === 'object') return formatOne(v);
  return String(v);
}

export default htmlToText;
